/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import React, { useContext, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ThemeContext } from "../../App";

import {
    Box,
    AppBar,
    Link,
    Toolbar,
    List,
    CssBaseline,
    Typography,
    Divider,
    IconButton,
    ListItemIcon,
    ListItemText,
    Button,
    Tooltip,
    Drawer,
    Menu,
    MenuProps,
    MenuItem,
    ListItemButton,
    Stack,
    Collapse,
} from "@mui/material";

import MenuIcon from "@mui/icons-material/Menu";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import CategoryIcon from "@mui/icons-material/Category";
import AssignmentInd from "@mui/icons-material/AssignmentIndOutlined";
import BusinessIcon from "@mui/icons-material/Business";
import EngineeringIcon from "@mui/icons-material/Engineering";
import ArchiveIcon from "@mui/icons-material/Archive";
import InfoIcon from "@mui/icons-material/Info";
import {
    AccountTree,
    AirportShuttle,
    Brightness3,
    Brightness7,
    CompareArrows,
    ExpandLess,
    ExpandMore,
    Settings,
    Storage,
    Warehouse,
    Devices,
    AssignmentTurnedIn,
    Inventory,
    LocalShipping,
    ListAlt,
    MoneyOff,
    KeyboardReturn,
    Assessment,
} from "@mui/icons-material";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import LogoutIcon from "@mui/icons-material/Logout";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

import GridViewIcon from "@mui/icons-material/GridView";
import ViewSidebarIcon from "@mui/icons-material/ViewSidebar";

import { selectFullName, selectLastName, selectRoles } from "../auth/authSlice";
import { logoutHandler } from "../auth/authFunctions";

import { styled, alpha } from "@mui/material/styles";

import { useWindowDimensions } from "../../utils/Hooks";
import { Effect, Nullable } from "../../utils/Interfaces";
import NotificationsBell from "./NotificationsBell";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { AnyAction, Dispatch } from "@reduxjs/toolkit";
import { customScrollbarStyle } from "../../utils/Functions";

const drawerWidth = 240;

const StyledMenu = styled((props: MenuProps) => (
    <Menu
        elevation={0}
        anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
        }}
        transformOrigin={{
            vertical: "top",
            horizontal: "right",
        }}
        {...props}
    />
))(({ theme }) => ({
    "& .MuiPaper-root": {
        borderRadius: 0,
        marginTop: theme.spacing(1),
        color: "red",
        minWidth: 240,
        "& .MuiMenuItem-root": {
            "& .MuiSvgIcon-root": {
                fontSize: 18,
                color: theme.palette.text.secondary,
                marginRight: theme.spacing(1.5),
            },
            "&:active": {
                backgroundColor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
            },
        },
    },
}));

export interface NavItemProps {
    label: string;
    tooltip: string;
    icon: JSX.Element;
    path: string;
    offset: boolean;
    hidden?: boolean;
    otherActions: Effect;
}

function NavItem(props: NavItemProps) {
    const { label, tooltip, icon, path, offset, hidden, otherActions } = props;

    const navigate = useNavigate();
    const { pathname } = useLocation();

    if (hidden) {
        return <Box></Box>;
    }

    return (
        <Tooltip title={tooltip} placement="right">
            <ListItemButton
                selected={pathname.includes(path)}
                onClick={() => {
                    if (otherActions !== null && otherActions !== undefined) {
                        otherActions();
                    }
                    navigate(path);
                }}
            >
                <ListItemIcon sx={{ pl: offset ? 2 : 0 }}>{icon}</ListItemIcon>
                <ListItemText primary={label} />
            </ListItemButton>
        </Tooltip>
    );
}

export interface NavProps {
    children: JSX.Element | JSX.Element[];
    forceRender: Effect;
}

const Nav = (props: NavProps) => {
    const { children, forceRender } = props;

    const isDarkTheme = localStorage.getItem("techwiseTheme") === "dark";
    const useBorderlessTheme: boolean = localStorage.getItem("navTheme") === "borderless";

    const navigate = useNavigate();

    const { width } = useWindowDimensions();
    const isMobile = width <= 1200;
    const [mobileOpen, setMobileOpen] = useState<boolean>(false);

    const [servicesExpanded, setServicesExpanded] = useState<boolean>(false);
    const [projectsExpanded, setProjectsExpanded] = useState<boolean>(false);
    const [techniciansExpanded, setTechniciansExpanded] = useState<boolean>(false);
    const [materialsExpanded, setMaterialsExpanded] = useState<boolean>(false);
    const [adminExpanded, setAdminExpanded] = useState<boolean>(false);

    const { setTheme } = useContext(ThemeContext);

    const [userMenuOpen, setUserMenuOpen] = useState<boolean>(false);
    const [anchorEl, setAnchorEl] = React.useState<Nullable<HTMLElement>>(null);
    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
        setUserMenuOpen(true);
    };
    const handleClose = () => {
        setAnchorEl(null);
        setUserMenuOpen(false);
    };

    const dispatch: Dispatch<AnyAction> = useAppDispatch();

    const fullName: string = useAppSelector(selectFullName);
    const lastName: string = useAppSelector(selectLastName);
    const roles: string[] = useAppSelector(selectRoles);

    const toggleDarkTheme = () => {
        const theme: "dark" | "light" = isDarkTheme ? "light" : "dark";
        setTheme(theme);
        localStorage.setItem("techwiseTheme", theme);
    };

    const toggleNavTheme = () => {
        const navTheme: "classic" | "borderless" = useBorderlessTheme ? "classic" : "borderless";
        localStorage.setItem("navTheme", navTheme);
        forceRender();
    };

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const officeSideBar = (
        <React.Fragment key={0}>
            <ListItemButton onClick={() => setServicesExpanded(!servicesExpanded)}>
                <ListItemIcon>
                    <AirportShuttle />
                </ListItemIcon>
                <ListItemText primary={<Typography fontWeight="bold">SERVICE</Typography>} />
                {servicesExpanded ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
            <Collapse in={servicesExpanded} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                    <NavItem
                        label="Work Orders"
                        tooltip="Manage Work Orders."
                        icon={<AssignmentOutlinedIcon fontSize="small" />}
                        path="/work-orders"
                        otherActions={handleDrawerToggle}
                        offset
                    />
                    <NavItem
                        label="Archive"
                        tooltip="View Archived Work Orders."
                        icon={<ArchiveIcon fontSize="small" />}
                        path="/archive"
                        otherActions={handleDrawerToggle}
                        offset
                    />
                    <NavItem
                        label="Assignments"
                        tooltip="Manage technician assignment priorities for work orders."
                        icon={<AssignmentInd fontSize="small" />}
                        path="/assignments"
                        otherActions={handleDrawerToggle}
                        offset
                    />
                    <NavItem
                        label="Companies"
                        tooltip="Manage Companies"
                        icon={<BusinessIcon fontSize="small" />}
                        path="/companies"
                        otherActions={handleDrawerToggle}
                        offset
                    />
                    <NavItem
                        label="Service Locations"
                        tooltip="Manage service and project locations."
                        icon={<LocationOnOutlinedIcon fontSize="small" />}
                        path="/work-sites"
                        otherActions={handleDrawerToggle}
                        offset
                    />
                </List>
            </Collapse>
        </React.Fragment>
    );

    const projectsSideBar = (
        <React.Fragment key={1}>
            <ListItemButton onClick={() => setProjectsExpanded(!projectsExpanded)} disabled>
                <ListItemIcon>
                    <AccountTree />
                </ListItemIcon>
                <ListItemText primary={<Typography fontWeight="bold">PROJECTS</Typography>} />
                {projectsExpanded ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
            <Collapse in={projectsExpanded} timeout="auto" unmountOnExit>
                <List component="div" disablePadding></List>
            </Collapse>
        </React.Fragment>
    );

    const materialsSideBar = (
        <React.Fragment key={2}>
            <ListItemButton onClick={() => setMaterialsExpanded(!materialsExpanded)}>
                <ListItemIcon>
                    <CategoryIcon />
                </ListItemIcon>
                <ListItemText primary={<Typography fontWeight="bold">MATERIALS</Typography>} />
                {materialsExpanded ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
            <Collapse in={materialsExpanded} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                    <NavItem
                        label="Materials DB"
                        tooltip="View and Edit the Materials Database (Catalog)."
                        icon={<Storage fontSize="small" />}
                        path="/materials/database"
                        otherActions={handleDrawerToggle}
                        offset
                    />
                    <NavItem
                        label="Storage Locations"
                        tooltip="Create and Manage Storage Locations for Parts/Materials."
                        icon={<Warehouse fontSize="small" />}
                        path="/materials/techwise-offices"
                        otherActions={handleDrawerToggle}
                        offset
                    />
                    <NavItem
                        label="Material Transfers"
                        tooltip="Ingress, Audit or Transfer Parts/Materials."
                        icon={<CompareArrows fontSize="small" />}
                        path="/materials/transfers"
                        otherActions={handleDrawerToggle}
                        offset
                    />
                    <NavItem
                        label="Office Restocking"
                        tooltip="View inventories that require restocking."
                        icon={<Inventory fontSize="small" />}
                        path="/materials/restocking/office"
                        otherActions={handleDrawerToggle}
                        offset
                    />
                    <NavItem
                        label="Tech. Restocking"
                        tooltip="View and quickly manage technician inventories for restocking."
                        icon={<LocalShipping fontSize="small" />}
                        path="/materials/restocking/technician"
                        otherActions={handleDrawerToggle}
                        offset
                    />
                    <NavItem
                        label="Transfer History"
                        tooltip="View and audit the Parts/Materials transfer history."
                        icon={<ListAlt fontSize="small" />}
                        path="/materials/history"
                        otherActions={handleDrawerToggle}
                        offset
                    />
                    <NavItem
                        label="RMAs"
                        tooltip="View and audit the Parts/Materials transfer history."
                        icon={<KeyboardReturn fontSize="small" />}
                        path="/materials/rma?status=OP"
                        otherActions={handleDrawerToggle}
                        offset
                    />
                    <NavItem
                        label="Losses"
                        tooltip="Audit reported material losses."
                        icon={<MoneyOff fontSize="small" />}
                        path="/materials/losses"
                        otherActions={handleDrawerToggle}
                        offset
                    />
                    <NavItem
                        label="Reports"
                        tooltip="View material sales reports."
                        icon={<Assessment fontSize="small" />}
                        path="/materials/reports"
                        otherActions={handleDrawerToggle}
                        offset
                    />
                </List>
            </Collapse>
        </React.Fragment>
    );

    const technicianSideBar = (
        <React.Fragment key={3}>
            <ListItemButton onClick={() => setTechniciansExpanded(!techniciansExpanded)}>
                <ListItemIcon>
                    <EngineeringIcon />
                </ListItemIcon>
                <ListItemText primary={<Typography fontWeight="bold">TECHNICIANS</Typography>} />
                {techniciansExpanded ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
            <Collapse in={techniciansExpanded} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                    <NavItem
                        label="Service Dashboard"
                        tooltip="Access your current and recently submitted assignments."
                        icon={<AirportShuttle />}
                        path="/tech-dash"
                        otherActions={handleDrawerToggle}
                        offset
                    />
                    <NavItem
                        label="My Assets"
                        tooltip="View your assets and initiate technician-to-technician asset transfers."
                        icon={<Devices />}
                        path="/assets/transfers"
                        otherActions={handleDrawerToggle}
                        offset
                    />
                    <NavItem
                        label="My Inventory"
                        tooltip="View your parts/materials inventory."
                        icon={<Inventory />}
                        path="/my-inventory"
                        otherActions={handleDrawerToggle}
                        offset
                    />
                    <NavItem
                        label="Inv. Transfer"
                        tooltip="Perform a technician-to-technician part/material transfer."
                        icon={<CompareArrows />}
                        path="/materials/tech-transfer"
                        otherActions={handleDrawerToggle}
                        offset
                    />
                </List>
            </Collapse>
        </React.Fragment>
    );

    const adminSideBar = (
        <React.Fragment key={4}>
            <ListItemButton onClick={() => setAdminExpanded(!adminExpanded)}>
                <ListItemIcon>
                    <SupervisorAccountIcon />
                </ListItemIcon>
                <ListItemText primary={<Typography fontWeight="bold">ADMIN</Typography>} />
                {adminExpanded ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
            <Collapse in={adminExpanded} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                    <NavItem
                        label="Manage Users"
                        tooltip="Manage user access, roles and information."
                        icon={<ManageAccountsIcon fontSize="small" />}
                        path="/admin/users"
                        otherActions={handleDrawerToggle}
                        offset
                    />
                    <NavItem
                        label="Manage Assets"
                        tooltip="Create and manage Techwise assets."
                        icon={<Devices fontSize="small" />}
                        path="/assets/manage"
                        otherActions={handleDrawerToggle}
                        offset
                    />
                    <NavItem
                        label="Assign Assets"
                        tooltip="Assign Techwise assets to users."
                        icon={<AssignmentTurnedIn fontSize="small" />}
                        path="/assets/assign"
                        otherActions={handleDrawerToggle}
                        offset
                    />
                    <NavItem
                        label="Global Config"
                        tooltip="Change global configuration settings."
                        icon={<Settings fontSize="small" />}
                        path="/admin/config"
                        otherActions={handleDrawerToggle}
                        offset
                    />
                    <NavItem
                        label="About"
                        tooltip="View portal details and documentation."
                        icon={<InfoIcon fontSize="small" />}
                        path="/about"
                        otherActions={handleDrawerToggle}
                        offset
                    />
                </List>
            </Collapse>
        </React.Fragment>
    );

    let navigation = [];
    if (roles.includes("office")) {
        navigation.push(officeSideBar);
    }
    if (roles.includes("projects") || roles.includes("technician")) {
        navigation.push(projectsSideBar);
    }
    if (roles.includes("materials")) {
        navigation.push(materialsSideBar);
    }
    if (roles.includes("technician")) {
        navigation.push(technicianSideBar);
    }
    if (roles.includes("admin")) {
        navigation.push(adminSideBar);
    }

    const drawer = (
        <Box>
            <Toolbar>
                <Link onClick={() => navigate("/")} sx={{ cursor: "pointer", width: "100%" }}>
                    <Stack direction="row" justifyContent="center" sx={{ width: "100%" }}>
                        <img
                            src="https://techwise-is-static-resources.s3.amazonaws.com/img/techwise-logo-owl.png"
                            alt="Techwise Owl Logo"
                            style={{ maxWidth: "100%" }}
                            height={56}
                            draggable={false}
                        />
                    </Stack>
                </Link>
            </Toolbar>
            <Divider />
            <List
                sx={{
                    maxHeight: "calc(100vh - 65px)",
                    minHeight: "calc(100vh - 65px)",
                    overflowY: "auto",
                    overflowX: "hidden",
                    ...customScrollbarStyle(),
                }}
            >
                {navigation.map((section) => section)}
            </List>
        </Box>
    );

    return (
        <Box sx={{ display: "flex" }}>
            <CssBaseline />
            <AppBar
                position="fixed"
                // @ts-ignore
                sx={{
                    width: { sm: `calc(100% - ${drawerWidth}px)` },
                    ml: { sm: `${drawerWidth}px` },
                    background: isDarkTheme
                        ? process.env.REACT_APP_BACKGROUND_COLOR_DARK
                        : process.env.REACT_APP_PRIMARY_COLOR_LIGHT,
                }}
            >
                <Toolbar>
                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        edge="start"
                        onClick={handleDrawerToggle}
                        sx={{ mr: 2, display: { sm: "none" } }}
                        size="large"
                    >
                        <MenuIcon />
                    </IconButton>
                    <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
                        {isMobile ? process.env.REACT_APP_TITLE_MOBILE : process.env.REACT_APP_TITLE_DESKTOP}
                    </Typography>

                    <Box sx={{ display: "flex", flexDirection: "row" }}>
                        <NotificationsBell />
                        <Button
                            variant="text"
                            id="demo-customized-button"
                            aria-controls={userMenuOpen ? "demo-customized-menu" : undefined}
                            aria-haspopup="true"
                            aria-expanded={userMenuOpen ? "true" : undefined}
                            disableElevation
                            onClick={handleClick}
                            endIcon={<KeyboardArrowDownIcon />}
                            color={"inherit"}
                        >
                            <Typography color="inherit" fontSize={20} marginRight={1}>
                                {isMobile ? lastName : fullName}
                            </Typography>
                        </Button>
                        <StyledMenu
                            id="demo-customized-menu"
                            MenuListProps={{
                                "aria-labelledby": "demo-customized-button",
                            }}
                            anchorEl={anchorEl}
                            open={userMenuOpen}
                            onClose={handleClose}
                        >
                            <MenuItem
                                onClick={() => {
                                    toggleDarkTheme();
                                    handleClose();
                                }}
                                disableRipple
                                sx={{ color: isDarkTheme ? "white" : "black" }}
                            >
                                {isDarkTheme ? <Brightness7 /> : <Brightness3 />}
                                {isDarkTheme ? "Enable Light Theme" : "Enable Dark Theme"}
                            </MenuItem>
                            <MenuItem
                                onClick={() => {
                                    toggleNavTheme();
                                    handleClose();
                                }}
                                disableRipple
                                sx={{ color: isDarkTheme ? "white" : "black" }}
                            >
                                {useBorderlessTheme ? <ViewSidebarIcon /> : <GridViewIcon />}
                                {useBorderlessTheme ? "Enable Classic Navigation" : "Enable Tile Navigation"}
                            </MenuItem>
                            <MenuItem
                                onClick={() => {
                                    logoutHandler(dispatch);
                                    handleClose();
                                }}
                                sx={{ color: isDarkTheme ? "white" : "black" }}
                                disableRipple
                            >
                                <LogoutIcon />
                                Logout
                            </MenuItem>
                        </StyledMenu>
                    </Box>
                </Toolbar>
            </AppBar>

            <Box component="nav" sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }} aria-label="nav">
                {isMobile && (
                    <Drawer
                        variant="temporary"
                        open={mobileOpen}
                        onClose={handleDrawerToggle}
                        ModalProps={{
                            keepMounted: true,
                        }}
                        sx={{
                            display: { xs: "block", sm: "none" },
                            "& .MuiDrawer-paper": {
                                boxSizing: "border-box",
                                width: drawerWidth,
                            },
                        }}
                    >
                        {drawer}
                    </Drawer>
                )}

                {!isMobile && (
                    <Drawer
                        variant="permanent"
                        sx={{
                            display: { xs: "none", sm: "block" },
                            "& .MuiDrawer-paper": {
                                boxSizing: "border-box",
                                width: drawerWidth,
                            },
                        }}
                        open={true}
                    >
                        {drawer}
                    </Drawer>
                )}
            </Box>
            <Box component="main" sx={{ flexGrow: 1, p: 2 }}>
                <Toolbar />
                {children}
            </Box>
        </Box>
    );
};

export default Nav;
